import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import CrmWorkspaceSettings from '../components/CrmWorkspaceSettings'
import CrmPipelineStageSettings from '../components/CrmPipelineStageSettings'

const EMPTY_TEMPLATE = {
  name: '',
  description: ''
}

const EMPTY_FIELD = {
  label: '',
  field_key: '',
  field_type: 'text',
  options: '',
  required: false
}

const FIELD_TYPES = ['text', 'date', 'url', 'select']

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export default function Settings() {
  const toast = useToast()
  const { org, orgId, can } = useUser()
  const canManageTemplates = can('templates.manage')

  const orgName = org?.name || 'Current organisation'

  const [templates, setTemplates] = useState([])
  const [fieldsByTemplate, setFieldsByTemplate] = useState({})
  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE)
  const [fieldForms, setFieldForms] = useState({})
  const [editingTemplate, setEditingTemplate] = useState(false)
  const [templateEditForm, setTemplateEditForm] = useState(EMPTY_TEMPLATE)
  const [editingFieldId, setEditingFieldId] = useState(null)
  const [fieldEditForm, setFieldEditForm] = useState(EMPTY_FIELD)
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [showNewTemplateForm, setShowNewTemplateForm] = useState(false)

  useEffect(() => {
    fetchTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  useEffect(() => {
    setEditingTemplate(false)
    setEditingFieldId(null)
  }, [selectedTemplateId])

  async function fetchTemplates(preferredTemplateId = null) {
    if (!orgId) return

    setLoadingTemplates(true)

    const { data: templateData, error: templateError } = await supabase
      .from('right_templates')
      .select('*')
      .eq('org_id', orgId)
      .eq('archived', false)
      .order('created_at', { ascending: true })

    if (templateError) {
      toast.error(`Failed to load right templates: ${templateError.message}`)
      setLoadingTemplates(false)
      return
    }

    const loadedTemplates = templateData || []
    const templateIds = loadedTemplates.map(t => t.id)

    let fieldData = []

    if (templateIds.length > 0) {
      const { data, error: fieldError } = await supabase
        .from('right_template_fields')
        .select('*')
        .in('template_id', templateIds)
        .order('sort_order', { ascending: true })

      if (fieldError) {
        toast.error(`Failed to load template fields: ${fieldError.message}`)
      }

      fieldData = data || []
    }

    const grouped = {}

    fieldData.forEach(field => {
      if (!grouped[field.template_id]) grouped[field.template_id] = []
      grouped[field.template_id].push(field)
    })

    setTemplates(loadedTemplates)
    setFieldsByTemplate(grouped)

    const stillExists = loadedTemplates.some(template => template.id === selectedTemplateId)

    if (preferredTemplateId) {
      setSelectedTemplateId(preferredTemplateId)
    } else if (!selectedTemplateId || !stillExists) {
      setSelectedTemplateId(loadedTemplates[0]?.id || null)
    }

    setLoadingTemplates(false)
  }

  async function createTemplate() {
    if (!templateForm.name.trim()) {
      toast.error('Template name is required')
      return
    }

    const { data, error } = await supabase
      .from('right_templates')
      .insert([{
        org_id: orgId,
        name: templateForm.name.trim(),
        description: templateForm.description || null
      }])
      .select()
      .single()

    if (error) {
      toast.error(`Failed to create template: ${error.message}`)
      return
    }

    toast.success('Right template created')
    setTemplateForm(EMPTY_TEMPLATE)
    setShowNewTemplateForm(false)
    fetchTemplates(data?.id || null)
  }

  function startTemplateEdit(template) {
    setTemplateEditForm({
      name: template.name || '',
      description: template.description || ''
    })
    setEditingTemplate(true)
  }

  async function updateTemplate(templateId) {
    if (!templateEditForm.name.trim()) {
      toast.error('Template name is required')
      return
    }

    const { error } = await supabase
      .from('right_templates')
      .update({
        name: templateEditForm.name.trim(),
        description: templateEditForm.description.trim() || null
      })
      .eq('id', templateId)
      .eq('org_id', orgId)

    if (error) {
      toast.error(`Failed to update template: ${error.message}`)
      return
    }

    toast.success('Template updated')
    setEditingTemplate(false)
    fetchTemplates(templateId)
  }

  async function deleteTemplate(templateId) {
    const confirmed = window.confirm(
      'Archive this template? Existing rights will still work.'
    )

    if (!confirmed) return

    const { error } = await supabase
      .from('right_templates')
      .update({ archived: true })
      .eq('id', templateId)
      .eq('org_id', orgId)

    if (error) {
      toast.error(`Failed to archive template: ${error.message}`)
      return
    }

    toast.success('Template archived')
    fetchTemplates()
  }

  function updateFieldForm(templateId, updates) {
    setFieldForms(prev => ({
      ...prev,
      [templateId]: {
        ...(prev[templateId] || EMPTY_FIELD),
        ...updates
      }
    }))
  }

  async function addField(templateId) {
    const form = fieldForms[templateId] || EMPTY_FIELD

    if (!form.label.trim()) {
      toast.error('Field label is required')
      return
    }

    const existingFields = fieldsByTemplate[templateId] || []
    const fieldKey = slugify(form.label)

    const options = form.field_type === 'select'
      ? form.options
          .split(',')
          .map(o => o.trim())
          .filter(Boolean)
      : []

    const { error } = await supabase
      .from('right_template_fields')
      .insert([{
        template_id: templateId,
        label: form.label.trim(),
        field_key: fieldKey,
        field_type: form.field_type,
        options,
        required: form.required,
        sort_order: existingFields.length
      }])

    if (error) {
      toast.error(`Failed to add field: ${error.message}`)
      return
    }

    toast.success('Field added')
    setFieldForms(prev => ({
      ...prev,
      [templateId]: EMPTY_FIELD
    }))
    fetchTemplates(templateId)
  }

  function startFieldEdit(field) {
    setFieldEditForm({
      label: field.label || '',
      field_key: field.field_key || '',
      field_type: field.field_type || 'text',
      options: Array.isArray(field.options) ? field.options.join(', ') : String(field.options || ''),
      required: !!field.required
    })
    setEditingFieldId(field.id)
  }

  async function updateField(fieldId) {
    if (!fieldEditForm.label.trim()) {
      toast.error('Field label is required')
      return
    }

    const options = fieldEditForm.field_type === 'select'
      ? fieldEditForm.options
          .split(',')
          .map(o => o.trim())
          .filter(Boolean)
      : []

    const { error } = await supabase
      .from('right_template_fields')
      .update({
        label: fieldEditForm.label.trim(),
        field_type: fieldEditForm.field_type,
        options,
        required: fieldEditForm.required
      })
      .eq('id', fieldId)
      .eq('template_id', selectedTemplateId)

    if (error) {
      toast.error(`Failed to update field: ${error.message}`)
      return
    }

    toast.success('Field updated')
    setEditingFieldId(null)
    setFieldEditForm(EMPTY_FIELD)
    fetchTemplates(selectedTemplateId)
  }

  async function deleteField(fieldId) {
    const { error } = await supabase
      .from('right_template_fields')
      .delete()
      .eq('id', fieldId)
      .eq('template_id', selectedTemplateId)

    if (error) {
      toast.error(`Failed to delete field: ${error.message}`)
      return
    }

    toast.success('Field removed')
    fetchTemplates(selectedTemplateId)
  }

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId)
  const selectedFields = fieldsByTemplate[selectedTemplateId] || []
  const selectedFieldForm = fieldForms[selectedTemplateId] || EMPTY_FIELD

  return (
    <div className="hv-page" style={{ maxWidth: '1200px' }}>
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">Settings</h1>
          <p className="hv-page-subtitle">View your organisation and manage right templates</p>
        </div>
      </div>

      {!canManageTemplates && (
        <div style={{
          background: 'var(--hv-amber-bg)',
          border: '1px solid var(--hv-amber)',
          borderRadius: 'var(--hv-radius)',
          padding: '12px 16px',
          fontSize: '13.5px',
          color: 'var(--hv-amber-text)',
          marginBottom: '24px'
        }}>
          Only admins can manage right templates.
        </div>
      )}

      <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--hv-text-primary)', marginBottom: '16px' }}>
          Organisation
        </div>

        <div className="hv-form-group">
          <label className="hv-form-label">Organisation Name</label>
          <div
            style={{
              minHeight: 36,
              border: '1px solid var(--hv-border)',
              borderRadius: '10px',
              background: '#F8FAFC',
              color: 'var(--hv-text-primary)',
              display: 'flex',
              alignItems: 'center',
              padding: '0 12px',
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            {orgName}
          </div>
          <div style={{ fontSize: 12, color: 'var(--hv-text-muted)', marginTop: 8 }}>
            Organisation names are managed by platform administrators.
          </div>
        </div>
      </div>

      <CrmWorkspaceSettings />
      <CrmPipelineStageSettings />

      <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--hv-text-primary)', marginBottom: '6px' }}>
              Right Templates
            </div>

            <p style={{ fontSize: '13px', color: 'var(--hv-text-muted)', margin: 0 }}>
              Create reusable right categories and define the fields shown in delivery tables.
            </p>
          </div>

          {canManageTemplates && (
            <button
              className="hv-btn hv-btn-primary"
              onClick={() => setShowNewTemplateForm(prev => !prev)}
            >
              {showNewTemplateForm ? 'Cancel' : '+ New Template'}
            </button>
          )}
        </div>

        {showNewTemplateForm && canManageTemplates && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.4fr auto',
            gap: '12px',
            alignItems: 'end',
            marginBottom: '24px',
            padding: '16px',
            border: '1px solid var(--hv-border)',
            borderRadius: 'var(--hv-radius)',
            background: '#FAFBFD'
          }}>
            <div>
              <label className="hv-form-label">Template Name</label>
              <input
                className="hv-input"
                placeholder="e.g. Social Media"
                value={templateForm.name}
                onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Description</label>
              <input
                className="hv-input"
                placeholder="Optional description..."
                value={templateForm.description}
                onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })}
              />
            </div>

            <button className="hv-btn hv-btn-primary" onClick={createTemplate}>
              Create
            </button>
          </div>
        )}

        {loadingTemplates ? (
          <div style={{ color: 'var(--hv-text-muted)', fontSize: '13px' }}>
            Loading templates...
          </div>
        ) : templates.length === 0 ? (
          <div className="hv-empty">
            <div className="hv-empty-title">No right templates yet</div>
            <div className="hv-empty-text">
              Create templates like Social Media, Hospitality, Branding or Speaking Slot.
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '280px minmax(0, 1fr)',
            gap: '24px',
            alignItems: 'start'
          }}>
            <div style={{
              border: '1px solid var(--hv-border)',
              borderRadius: 'var(--hv-radius-lg)',
              background: '#fff',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '14px 16px',
                borderBottom: '1px solid var(--hv-border)',
                fontSize: '11px',
                fontWeight: '800',
                color: 'var(--hv-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em'
              }}>
                Templates
              </div>

              <div style={{ padding: '10px' }}>
                {templates.map(template => {
                  const fields = fieldsByTemplate[template.id] || []
                  const isSelected = selectedTemplateId === template.id

                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => setSelectedTemplateId(template.id)}
                      style={{
                        width: '100%',
                        border: '1px solid transparent',
                        borderRadius: '12px',
                        padding: '12px',
                        marginBottom: '6px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        background: isSelected ? '#F5F3FF' : 'transparent',
                        borderColor: isSelected ? '#DDD6FE' : 'transparent'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '10px',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '800',
                          color: isSelected ? '#4F46E5' : 'var(--hv-text-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {template.name}
                        </div>

                        <span className="hv-badge hv-badge-gray" style={{ flexShrink: 0 }}>
                          {fields.length}
                        </span>
                      </div>

                      <div style={{
                        fontSize: '12px',
                        color: 'var(--hv-text-muted)',
                        marginTop: '5px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {fields.length === 1 ? '1 field' : `${fields.length} fields`}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div style={{
              border: '1px solid var(--hv-border)',
              borderRadius: 'var(--hv-radius-lg)',
              background: '#FAFBFD',
              padding: '20px',
              minHeight: '520px'
            }}>
              {!selectedTemplate ? (
                <div className="hv-empty">
                  <div className="hv-empty-title">Select a template</div>
                  <div className="hv-empty-text">Choose a template from the left to manage its fields.</div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '18px' }}>
                    {editingTemplate ? (
                      <div style={{
                        flex: 1,
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)',
                        gap: '12px'
                      }}>
                        <div>
                          <label className="hv-form-label">Template Name</label>
                          <input
                            className="hv-input"
                            value={templateEditForm.name}
                            onChange={e => setTemplateEditForm({ ...templateEditForm, name: e.target.value })}
                          />
                        </div>

                        <div>
                          <label className="hv-form-label">Description</label>
                          <input
                            className="hv-input"
                            value={templateEditForm.description}
                            onChange={e => setTemplateEditForm({ ...templateEditForm, description: e.target.value })}
                            placeholder="Optional description..."
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: 'var(--hv-text-primary)', marginBottom: '5px' }}>
                          {selectedTemplate.name}
                        </div>

                        <div style={{ fontSize: '13px', color: 'var(--hv-text-muted)', lineHeight: 1.5 }}>
                          {selectedTemplate.description || 'No description'}
                        </div>
                      </div>
                    )}

                    {canManageTemplates && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexShrink: 0 }}>
                        {editingTemplate ? (
                          <>
                            <button
                              className="hv-btn hv-btn-secondary"
                              style={{ fontSize: '12px' }}
                              onClick={() => setEditingTemplate(false)}
                            >
                              Cancel
                            </button>
                            <button
                              className="hv-btn hv-btn-primary"
                              style={{ fontSize: '12px' }}
                              onClick={() => updateTemplate(selectedTemplate.id)}
                            >
                              Save Template
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="hv-btn hv-btn-secondary"
                              style={{ fontSize: '12px' }}
                              onClick={() => startTemplateEdit(selectedTemplate)}
                            >
                              Edit Template
                            </button>
                            <button
                              className="hv-btn hv-btn-danger"
                              style={{ fontSize: '12px' }}
                              onClick={() => deleteTemplate(selectedTemplate.id)}
                            >
                              Archive Template
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{
                    background: '#fff',
                    border: '1px solid var(--hv-border)',
                    borderRadius: 'var(--hv-radius)',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--hv-text-primary)' }}>
                        Fields
                      </div>

                      <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)' }}>
                        {selectedFields.length === 1 ? '1 field' : `${selectedFields.length} fields`}
                      </div>
                    </div>

                    {selectedFields.length === 0 ? (
                      <div style={{
                        border: '1px dashed var(--hv-border)',
                        borderRadius: 'var(--hv-radius)',
                        padding: '24px',
                        textAlign: 'center',
                        fontSize: '13px',
                        color: 'var(--hv-text-muted)'
                      }}>
                        No fields added yet. Add your first field below.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {selectedFields.map(field => {
                          const isEditingField = editingFieldId === field.id

                          return (
                            <div
                              key={field.id}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: isEditingField
                                  ? 'minmax(0, 1.2fr) 120px minmax(0, 1.3fr) auto auto auto'
                                  : 'minmax(0, 1fr) 110px minmax(0, 1fr) auto auto',
                                gap: '10px',
                                alignItems: 'center',
                                padding: '10px 12px',
                                background: '#fff',
                                border: '1px solid var(--hv-border)',
                                borderRadius: 'var(--hv-radius-sm)'
                              }}
                            >
                              {isEditingField ? (
                                <>
                                  <div>
                                    <label className="hv-form-label">Field Label</label>
                                    <input
                                      className="hv-input"
                                      value={fieldEditForm.label}
                                      onChange={e => setFieldEditForm({ ...fieldEditForm, label: e.target.value })}
                                    />
                                    <div style={{ fontSize: '11px', color: 'var(--hv-text-muted)', marginTop: '5px' }}>
                                      Key: {field.field_key}
                                    </div>
                                  </div>

                                  <div>
                                    <label className="hv-form-label">Type</label>
                                    <select
                                      className="hv-select"
                                      value={fieldEditForm.field_type}
                                      onChange={e => setFieldEditForm({ ...fieldEditForm, field_type: e.target.value })}
                                    >
                                      {FIELD_TYPES.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label className="hv-form-label">Options</label>
                                    <input
                                      className="hv-input"
                                      value={fieldEditForm.options}
                                      disabled={fieldEditForm.field_type !== 'select'}
                                      onChange={e => setFieldEditForm({ ...fieldEditForm, options: e.target.value })}
                                      placeholder="Twitter, Instagram, YouTube"
                                    />
                                  </div>

                                  <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '12.5px',
                                    color: 'var(--hv-text-secondary)'
                                  }}>
                                    <input
                                      type="checkbox"
                                      checked={fieldEditForm.required}
                                      onChange={e => setFieldEditForm({ ...fieldEditForm, required: e.target.checked })}
                                    />
                                    Required
                                  </label>

                                  <button
                                    className="hv-btn hv-btn-secondary"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => setEditingFieldId(null)}
                                  >
                                    Cancel
                                  </button>

                                  <button
                                    className="hv-btn hv-btn-primary"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => updateField(field.id)}
                                  >
                                    Save
                                  </button>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--hv-text-primary)' }}>
                                      {field.label}
                                      {field.required && <span style={{ color: 'var(--hv-red)' }}> *</span>}
                                    </div>
                                    <div style={{ fontSize: '11px', color: 'var(--hv-text-muted)' }}>
                                      {field.field_key}
                                    </div>
                                  </div>

                                  <span className="hv-badge hv-badge-gray">
                                    {field.field_type}
                                  </span>

                                  <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {Array.isArray(field.options) && field.options.length > 0
                                      ? field.options.join(', ')
                                      : '—'}
                                  </div>

                                  {canManageTemplates && (
                                    <>
                                      <button className="hv-btn hv-btn-secondary" style={{ fontSize: '12px' }} onClick={() => startFieldEdit(field)}>
                                        Edit
                                      </button>

                                      <button className="hv-btn hv-btn-danger" style={{ fontSize: '12px' }} onClick={() => deleteField(field.id)}>
                                        Remove
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {canManageTemplates && (
                    <div style={{
                      background: '#fff',
                      border: '1px solid var(--hv-border)',
                      borderRadius: 'var(--hv-radius)',
                      padding: '16px'
                    }}>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--hv-text-primary)', marginBottom: '12px' }}>
                        Add Field
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1.4fr 130px 1.4fr auto auto',
                        gap: '10px',
                        alignItems: 'end'
                      }}>
                        <div>
                          <label className="hv-form-label">Field Label</label>
                          <input
                            className="hv-input"
                            placeholder="e.g. Channel"
                            value={selectedFieldForm.label}
                            onChange={e => updateFieldForm(selectedTemplate.id, { label: e.target.value })}
                          />
                        </div>

                        <div>
                          <label className="hv-form-label">Type</label>
                          <select
                            className="hv-select"
                            value={selectedFieldForm.field_type}
                            onChange={e => updateFieldForm(selectedTemplate.id, { field_type: e.target.value })}
                          >
                            {FIELD_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="hv-form-label">Options</label>
                          <input
                            className="hv-input"
                            placeholder="Primary social channel, video channel, newsletter"
                            value={selectedFieldForm.options}
                            disabled={selectedFieldForm.field_type !== 'select'}
                            onChange={e => updateFieldForm(selectedTemplate.id, { options: e.target.value })}
                          />
                        </div>

                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12.5px',
                          color: 'var(--hv-text-secondary)',
                          paddingBottom: '9px'
                        }}>
                          <input
                            type="checkbox"
                            checked={selectedFieldForm.required}
                            onChange={e => updateFieldForm(selectedTemplate.id, { required: e.target.checked })}
                          />
                          Required
                        </label>

                        <button className="hv-btn hv-btn-secondary" onClick={() => addField(selectedTemplate.id)}>
                          Add Field
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
